import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { Calendar, Clock, Users, AlertTriangle, ArrowRight, TrendingUp, LayoutDashboard, ArrowLeftRight, FileText, Bell, Shield } from 'lucide-react';
import { isManager } from './utils/permissions';

export default function Dashboard({ user, userData, companyId }) {
  const { t } = useTranslation();
  const [todaysShifts, setTodaysShifts] = useState([]);
  const [upcomingShifts] = useState([]);
  const [pendingSwaps, setPendingSwaps] = useState([]);
  const [stats] = useState({ totalStaff: 0, totalShiftsThisWeek: 0, coverageRate: 0 });
  const [loading, setLoading] = useState(true);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (!companyId) return;

    // Subscribe to today's shifts
    const todayQuery = query(
      collection(db, 'companies', companyId, 'shifts'),
      where('date', '==', today),
      orderBy('startTime')
    );

    const unsubToday = onSnapshot(todayQuery, (snapshot) => {
      setTodaysShifts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, (error) => {
      console.error('Error listening to shifts:', error);
      setLoading(false);
    });

    // Subscribe to pending swaps
    const swapsQuery = query(
      collection(db, 'companies', companyId, 'swapRequests'),
      where('status', '==', 'pending')
    );

    const unsubSwaps = onSnapshot(swapsQuery, (snapshot) => {
      setPendingSwaps(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      console.error('Error listening to swap requests:', error);
    });

    return () => {
      unsubToday();
      unsubSwaps();
    };
  }, [companyId, today]);

  const displayName = userData?.displayName || userData?.username || user?.email?.split('@')[0] || 'User';

  return (
    <div className="space-y-6">
      {/* Welcome header */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-2">
          <img src="/logo.png" alt="BuildMyShifts" className="w-10 h-10 object-contain" />
          <h1 className="text-2xl font-bold text-white">
            {t('dashboard.welcome', { name: displayName })}
          </h1>
        </div>
        <p className="text-gray-400 mt-1">{t('app.description')}</p>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Calendar className="text-primary" size={24} />}
          label={t('dashboard.todaysShifts')}
          value={todaysShifts.length}
          color="bg-blue-500/10"
        />
        <StatCard
          icon={<Users className="text-blue-500" size={24} />}
          label={t('dashboard.pendingSwaps')}
          value={pendingSwaps.length}
          color="bg-blue-500/10"
        />
        <StatCard
          icon={<Clock className="text-green-500" size={24} />}
          label={t('dashboard.upcomingShifts')}
          value={upcomingShifts.length}
          color="bg-green-500/10"
        />
        <StatCard
          icon={<TrendingUp className="text-purple-500" size={24} />}
          label={t('analytics.coverageRate')}
          value={`${stats.coverageRate}%`}
          color="bg-purple-500/10"
        />
      </div>

      {/* Today's shifts */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Calendar size={20} className="text-primary" />
          {t('dashboard.todaysShifts')}
        </h2>
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : todaysShifts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {t('dashboard.noShiftsToday')}
          </div>
        ) : (
          <div className="space-y-3">
            {todaysShifts.map(shift => (
              <ShiftCard key={shift.id} shift={shift} />
            ))}
          </div>
        )}
      </div>

      {/* Pending swaps (for managers) */}
      {isManager(userData) && pendingSwaps.length > 0 && (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <AlertTriangle size={20} className="text-yellow-500" />
            {t('dashboard.pendingSwaps')} ({pendingSwaps.length})
          </h2>
          <div className="space-y-2">
            {pendingSwaps.slice(0, 5).map(swap => (
              <div key={swap.id} className="flex items-center justify-between p-3 bg-yellow-500/10 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-white">{swap.requestedByName}</p>
                  <p className="text-xs text-gray-400">{swap.shiftDate} — {swap.reason}</p>
                </div>
                <ArrowRight size={16} className="text-gray-400" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">{t('dashboard.quickActions')}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {isManager(userData) && (
            <QuickAction
              icon={<Calendar size={20} />}
              label={t('dashboard.createRota')}
              href="/rotas"
              color="bg-primary text-white hover:bg-primary-dark"
            />
          )}
          <QuickAction
            icon={<Clock size={20} />}
            label={t('dashboard.viewRota')}
            href="/rotas"
            color="bg-secondary text-white hover:bg-secondary-dark"
          />
          <QuickAction
            icon={<LayoutDashboard size={20} />}
            label={t('nav.myDashboard', 'My Shifts')}
            href="/my-dashboard"
            color="bg-indigo-600 text-white hover:bg-indigo-700"
          />
          <QuickAction
            icon={<ArrowLeftRight size={20} />}
            label={t('nav.swapBoard', 'Swap Board')}
            href="/swap-board"
            color="bg-amber-600 text-white hover:bg-amber-700"
          />
          <QuickAction
            icon={<Bell size={20} />}
            label={t('nav.notifications', 'Notifications')}
            href="/notifications"
            color="bg-cyan-600 text-white hover:bg-cyan-700"
          />
          {isManager(userData) && (
            <QuickAction
              icon={<Users size={20} />}
              label={t('dashboard.manageStaff')}
              href="/staff"
              color="bg-gray-800 text-white hover:bg-gray-900"
            />
          )}
          {isManager(userData) && (
            <QuickAction
              icon={<FileText size={20} />}
              label={t('nav.reports', 'Reports')}
              href="/reports"
              color="bg-emerald-600 text-white hover:bg-emerald-700"
            />
          )}
          {isManager(userData) && (
            <QuickAction
              icon={<Shield size={20} />}
              label={t('nav.auditLog', 'Audit Log')}
              href="/audit-log"
              color="bg-rose-600 text-white hover:bg-rose-700"
            />
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div className={`${color} rounded-xl p-4 flex items-center gap-4`}>
      <div className="p-2 bg-white/10 rounded-lg">{icon}</div>
      <div>
        <p className="text-2xl font-bold text-white">{value}</p>
        <p className="text-sm text-gray-400">{label}</p>
      </div>
    </div>
  );
}

function ShiftCard({ shift }) {
  const statusColors = {
    assigned: 'bg-blue-100 text-blue-700',
    confirmed: 'bg-green-100 text-green-700',
    unassigned: 'bg-white/10 text-gray-300',
    declined: 'bg-red-100 text-red-700',
    cancelled: 'bg-white/15 text-gray-400',
  };

  return (
    <div className="shift-card flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10">
      <div className="flex items-center gap-3">
        <div className="text-center">
          <p className="text-lg font-bold text-primary">{shift.startTime}</p>
          <p className="text-xs text-gray-400">to {shift.endTime}</p>
        </div>
        <div>
          <p className="font-medium text-white">{shift.assignedStaffName || 'Unassigned'}</p>
          <p className="text-sm text-gray-400">{shift.requiredRole || 'General'}{shift.location ? ` — ${shift.location}` : ''}</p>
        </div>
      </div>
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[shift.status] || statusColors.unassigned}`}>
        {shift.status || 'unassigned'}
      </span>
    </div>
  );
}

function QuickAction({ icon, label, href, color }) {
  return (
    <a
      href={href}
      className={`${color} flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors text-sm`}
    >
      {icon}
      {label}
    </a>
  );
}
